<template>
  <div class="badge-print-page">
    <div class="screen-only toolbar">
      <span class="toolbar-title">
        Dienstausweis{{ fullName ? ' – ' + fullName : '' }}
      </span>
      <button v-if="badgeReady" type="button" class="print-button" @click="print">
        <i class="fa fa-print mr-1"></i> Drucken
      </button>
    </div>
    <div v-if="loading" class="screen-only status">Lade Mitarbeiterdaten…</div>
    <div v-else-if="error" class="screen-only status error">{{ error }}</div>
    <div v-show="badgeReady" class="badge-stage">
      <div ref="badge" class="badge">
        <div class="badge-header">S E C U R I T Y</div>
        <div class="badge-title">D I E N S T A U S W E I S</div>
        <img class="badge-logo" src="@/assets/casplogo.png" alt="CASP" />
        <img v-if="qrDataUrl" class="badge-qr" :src="qrDataUrl" alt="QR" />
        <div class="badge-web">www.casp-security.com</div>
        <div class="badge-field" style="top: 42.8mm">
          <div class="badge-value">{{ user.function }}</div>
          <div class="badge-line"></div>
          <div class="badge-label">Funktion</div>
        </div>
        <div class="badge-field" style="top: 51.97mm">
          <div class="badge-value">{{ user.idbewar }}</div>
          <div class="badge-line"></div>
          <div class="badge-label">BEWACHER-ID</div>
        </div>
        <div class="badge-field" style="top: 60.97mm">
          <div class="badge-value">{{ user.idnr }}</div>
          <div class="badge-line"></div>
          <div class="badge-label">AUSWEIS-NR</div>
        </div>
        <div class="badge-ma">MA: {{ fullName }}</div>
        <img class="badge-footer" src="@/assets/badgefooter.png" alt="" />
      </div>
    </div>
  </div>
</template>

<script>
import CASP from "@/CASP.js";
import QRious from "qrious";

// Badge dimensions and element positions match the official template
// "CASP Dienstausweis Vorderseite.pptx" (54.11mm x 85.11mm portrait card).
export default {
  name: "BadgePrint",
  data() {
    return {
      loading: true,
      error: "",
      user: {},
      qrDataUrl: "",
      printed: false,
    };
  },
  computed: {
    fullName() {
      return [this.user.name, this.user.surname].filter(Boolean).join(" ").trim();
    },
    badgeReady() {
      return !this.loading && !this.error;
    },
  },
  mounted() {
    const spinner = document.getElementById("mainspinner");
    if (spinner) spinner.style.display = "none";
    // The @page size must only apply while this view is open; a static global
    // style would leak into other pages' print output for the whole session.
    this.pageStyle = document.createElement("style");
    this.pageStyle.textContent =
      "@page { size: 54.11mm 85.11mm; margin: 0; }" +
      "@media print { html, body { margin: 0; padding: 0; width: 54.11mm; height: 85.11mm; } }";
    document.head.appendChild(this.pageStyle);
    CASP.getUser(this.$route.params.userId, (status, response) => {
      this.loading = false;
      if (status != 200) {
        this.error = "Mitarbeiter konnte nicht geladen werden.";
        return;
      }
      this.user = response;
      if (this.user.qrValue) {
        this.qrDataUrl = new QRious({
          value: this.user.qrValue,
          size: 400,
          padding: 0,
        }).toDataURL();
      }
      this.printWhenReady();
    });
  },
  unmounted() {
    if (this.pageStyle) this.pageStyle.remove();
  },
  methods: {
    print() {
      window.print();
    },
    async printWhenReady() {
      await this.$nextTick();
      const images = this.$refs.badge ? this.$refs.badge.querySelectorAll("img") : [];
      await Promise.all(
        Array.from(images).map((img) =>
          img.complete
            ? Promise.resolve()
            : new Promise((resolve) => {
                img.onload = resolve;
                img.onerror = resolve;
              })
        )
      );
      if (!this.printed) {
        this.printed = true;
        window.print();
      }
    },
  },
};
</script>

<style scoped>
.badge-print-page {
  min-height: 100vh;
  background: #e5e7eb;
  text-align: center;
}

.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 16px;
  background: #171a1f;
  color: #fff;
  font-weight: 600;
}

.print-button {
  background: #ffed00;
  color: #171a1f;
  border: none;
  border-radius: 6px;
  padding: 6px 14px;
  font-weight: 700;
  cursor: pointer;
}

.status {
  padding: 24px;
  font-size: 15px;
}

.status.error {
  color: #b91c1c;
  font-weight: 600;
}

.badge-stage {
  /* scaled preview size: badge mm size x PREVIEW_SCALE */
  width: 135.3mm;
  height: 212.8mm;
  margin: 24px auto;
}

.badge {
  position: relative;
  width: 54.11mm;
  height: 85.11mm;
  background: #fff;
  overflow: hidden;
  font-family: Arial, Helvetica, sans-serif;
  color: #000;
  text-align: left;
  transform: scale(2.5);
  transform-origin: top left;
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.35);
  /* The yellow SECURITY bar is a CSS background; without this the browser
     would drop it during printing and leave a white strip on the card. */
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

.badge-header {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 7.03mm;
  background: #ffff00;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11pt;
  font-weight: bold;
}

.badge-title {
  position: absolute;
  top: 7.25mm;
  left: 0;
  width: 100%;
  text-align: center;
  font-size: 11pt;
}

.badge-logo {
  position: absolute;
  top: 17.54mm;
  left: 6.27mm;
  width: 19.81mm;
  height: 13mm;
  object-fit: contain;
}

.badge-qr {
  position: absolute;
  top: 17.26mm;
  left: 35.08mm;
  width: 14.02mm;
  height: 14.02mm;
}

.badge-web {
  position: absolute;
  top: 34.01mm;
  left: 0;
  width: 100%;
  text-align: center;
  font-size: 9pt;
}

.badge-field {
  position: absolute;
  left: 2.12mm;
  width: 26.46mm;
}

.badge-value {
  height: 4.24mm;
  display: flex;
  align-items: flex-end;
  font-size: 7pt;
  font-weight: bold;
  line-height: 1.1;
  white-space: nowrap;
  overflow: hidden;
}

.badge-line {
  height: 0;
  border-bottom: 0.3mm solid #000;
}

.badge-label {
  font-size: 7pt;
}

.badge-ma {
  position: absolute;
  top: 71.42mm;
  left: 1.84mm;
  width: 48.83mm;
  font-size: 6pt;
}

.badge-footer {
  position: absolute;
  top: 78.19mm;
  left: 0;
  width: 54.11mm;
  height: 6.95mm;
  object-fit: fill;
}

@media print {
  .screen-only {
    display: none !important;
  }

  .badge-print-page {
    background: #fff;
    min-height: 0;
  }

  .badge-stage {
    width: auto;
    height: auto;
    margin: 0;
  }

  .badge {
    transform: none;
    box-shadow: none;
  }
}
</style>
